import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import Header from '../components/layout/Header';
import CurrencyManagement from '../components/setup/CurrencyManagement';
import FileTagsManagement from '../components/setup/FileTagsManagement';
import DataUsageProgress from '../components/setup/DataUsageProgress'; // 📊 NOVÝ IMPORT
import { companySettings } from '../services/api';
import { useTranslation } from 'react-i18next';

const CompanySetupPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  
  // ✅ FIXED: Better default values with safeguards
  const [companyData, setCompanyData] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    phone: '',
    email: '',
    taxId: '',
    defaultCurrency: 'USD',
    enabledCurrencies: [
      // ✅ DEFAULT: Ensure at least one currency is always available
      { code: 'USD', name: 'US Dollar', symbol: '$', enabled: true },
      { code: 'EUR', name: 'Euro', symbol: '€', enabled: false },
      { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', enabled: false }
    ],
    fileTags: [
      // ✅ DEFAULT: Ensure default file tags
      { code: 'all', label: 'All Files', enabled: true },
      { code: 'document', label: 'Documents', enabled: true },
      { code: 'inspection', label: 'Inspection', enabled: true },
      { code: 'implementation', label: 'Implementation', enabled: true },
      { code: 'contract', label: 'Contracts', enabled: true }
    ]
  });

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      const { data } = await companySettings.get();
      
      console.log('📊 Received company data:', data);
      
      // ✅ DEFENSIVE: Ensure enabledCurrencies exists and has at least one item
      const enabledCurrencies = data.enabledCurrencies || [
        { code: 'USD', name: 'US Dollar', symbol: '$', enabled: true },
        { code: 'EUR', name: 'Euro', symbol: '€', enabled: false },
        { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', enabled: false }
      ];
      
      // ✅ DEFENSIVE: Ensure at least one currency is enabled
      const hasEnabledCurrency = enabledCurrencies.some(c => c.enabled);
      if (!hasEnabledCurrency && enabledCurrencies.length > 0) {
        enabledCurrencies[0].enabled = true;
      }
      
      // ✅ DEFENSIVE: Ensure default file tags exist
      let fileTags = data.fileTags || [];
      if (fileTags.length === 0) {
        fileTags = [
          { code: 'all', label: 'All Files', enabled: true },
          { code: 'document', label: 'Documents', enabled: true },
          { code: 'inspection', label: 'Inspection', enabled: true },
          { code: 'implementation', label: 'Implementation', enabled: true },
          { code: 'contract', label: 'Contracts', enabled: true }
        ];
      }
      
      // ✅ MERGE: Combine API data with safe defaults - ZACHOVÁNO PŮVODNÍ API
      setCompanyData({
        name: data.name || '',
        address: data.address || '',
        city: data.city || '',
        country: data.country || '',
        postalCode: data.postalCode || '',
        phone: data.phone || '',
        email: data.email || '',
        taxId: data.taxId || '',
        defaultCurrency: data.defaultCurrency || 'USD',
        enabledCurrencies: enabledCurrencies,
        fileTags: fileTags
      });
      
    } catch (err) {
      console.error('❌ Failed to load company data:', err);
      setError(err.response?.data?.message || 'Failed to load company data');
      
      // ✅ FALLBACK: Keep default values if API fails
      console.log('🛡️ Using default company data due to API error');
      
    } finally {
      setLoading(false);
    }
  };

  // ZACHOVANÉ PŮVODNÍ HANDLERY
  const handleCurrencyToggle = (currency) => {
    // ✅ DEFENSIVE: Ensure enabledCurrencies is always an array
    const enabledCurrencies = companyData.enabledCurrencies || [];
    const existingCurrency = enabledCurrencies.find(c => c.code === currency.code);
    
    if (existingCurrency) {
      // If currency exists, toggle its enabled status
      setCompanyData({
        ...companyData,
        enabledCurrencies: enabledCurrencies.map(c => 
          c.code === currency.code 
            ? { ...c, enabled: !c.enabled }
            : c
        )
      });
    } else {
      // If currency doesn't exist, add it as enabled
      setCompanyData({
        ...companyData,
        enabledCurrencies: [
          ...enabledCurrencies,
          { ...currency, enabled: true }
        ]
      });
    }
  };

  const handleDefaultCurrencyChange = (currencyCode) => {
    setCompanyData({
      ...companyData,
      defaultCurrency: currencyCode
    });
  };

  // Handler for file tags
  const handleFileTagsChange = (tags) => {
    setCompanyData({
      ...companyData,
      fileTags: tags || []
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyData({
      ...companyData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaved(false);

    // Check for required fields
    const requiredFields = [
      'name', 'address', 'city', 'country', 'postalCode', 
      'phone', 'email', 'taxId', 'defaultCurrency'
    ];
    
    const emptyFields = requiredFields.filter(field => !companyData[field]);
    
    if (emptyFields.length > 0) {
      setError(t('companySetup.validation.requiredFields', { fields: emptyFields.join(', ') }));
      return;
    }

    try {
      console.log('💾 Saving company data:', companyData);

      // ✅ DEBUG: Log exactly what we're sending
      console.log('📊 Data structure check:', {
        name: companyData.name,
        enabledCurrencies: companyData.enabledCurrencies?.length,
        fileTags: companyData.fileTags?.length,
        fileTagsDetail: companyData.fileTags?.map(t => ({ code: t.code, labels: t.labels })),
        hasUndefined: Object.entries(companyData).filter(([key, value]) => value === undefined)
      });

      console.log('🚀 Calling companySettings.update...');
      const response = await companySettings.update(companyData);
      console.log('✅ Update response:', response);

      // ✅ Emit event to notify all components to refresh file tags
      window.dispatchEvent(new CustomEvent('fileTagsUpdated'));

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('❌ Save error:', err);
      console.error('📋 Full error object:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.message || 'Failed to save company data');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('companySetup.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('companySetup.description')}</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            <p className="font-medium">Error:</p>
            <p>{error}</p>
          </div>
        )}

        {saved && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6">
            <p>{t('companySetup.messages.saved')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Company Information */}
          <div className="bg-white shadow rounded-lg">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('companySetup.sections.basicInfo')}</h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    {t('companySetup.fields.companyName')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={companyData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="taxId" className="block text-sm font-medium text-gray-700">
                    {t('companySetup.fields.taxId')} *
                  </label>
                  <input
                    type="text"
                    name="taxId"
                    id="taxId"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={companyData.taxId}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                    {t('companySetup.fields.address')} *
                  </label>
                  <input
                    type="text"
                    name="address"
                    id="address"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={companyData.address}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                    {t('companySetup.fields.city')} *
                  </label>
                  <input
                    type="text"
                    name="city"
                    id="city"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={companyData.city}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                    {t('companySetup.fields.postalCode')} *
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    id="postalCode"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={companyData.postalCode}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                    {t('companySetup.fields.country')} *
                  </label>
                  <input
                    type="text"
                    name="country"
                    id="country"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={companyData.country}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    {t('companySetup.fields.phone')} *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={companyData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    {t('companySetup.fields.email')} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={companyData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 📊 NOVÁ SEKCE: Progress bar pro využití dat */}
          <div className="mt-6">
            <DataUsageProgress />
          </div>

          {/* Currency Settings */}
          <div className="bg-white shadow rounded-lg mt-6">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('companySetup.sections.currencySettings')}</h3>
              <CurrencyManagement
                currencies={companyData.enabledCurrencies}
                onCurrencyChange={handleCurrencyToggle}
                defaultCurrency={companyData.defaultCurrency}
                onDefaultChange={handleDefaultCurrencyChange}
              />
            </div>
          </div>

          {/* File Tags Settings */}
          <div className="bg-white shadow rounded-lg mt-6">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('companySetup.sections.fileCategories')}</h3>
              <FileTagsManagement
                tags={companyData.fileTags}
                onChange={handleFileTagsChange}
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Save className="h-4 w-4 mr-2" />
              {t('companySetup.buttons.save')}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CompanySetupPage;